// The approved-open-PR list's data layer: `GET /v1/approved-prs` — the
// account-wide PRs a human APPROVED that are still open, so the merge gate is
// waiting on them.
//
// The dashboard declares its OWN row type here, the `ApprovalRow` precedent in
// `lib/overview.ts`: the server publishes this row's schema in
// `@developerz/domain`, and the row this app holds is its own mirror of that
// schema — it takes no new domain export for the row.
//
// THE REFUSAL VOCABULARY IS NOT DECLARED HERE. It is the domain's closed set —
// `StoredMergeRefusalCode` (`value-objects/merge-refusal-posture.ts`), the very
// codes the wire publishes (`wire/overview.ts`) — IMPORTED, never restated. A
// hand-copied union is a COPY of a set, and a copy drifts from it with nothing
// red: the domain's set could gain a code and this app would go on reading the
// old two, with the label map below still total over its own stale copy. The
// map is now total over the DOMAIN's set, so that code is a compile error
// here, and `src/i18n.test.ts` holds the catalog side (a label that exists here
// but not in `locales/en.json` fails there rather than shipping `⟦key⟧`).
//
// WHAT "APPROVED" MEANS HERE. A human approved the PR (`merge_decision =
// 'auto'`) AND the PR is still open. A PR parked on `ask` is NOT on this list —
// it stays in the approvals queue (`/v1/approvals`), which this list does not
// touch. A closed or merged PR is not here either.

import type { StoredMergeRefusalCode } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/**
 * GitHub's tri-state mergeability — the stored `mergeable` column's three
 * words. A fourth would be a wire change, and it reads as "unknown" until the
 * catalog says otherwise.
 */
export type ApprovedPrMergeable = 'mergeable' | 'conflicting' | 'unknown';

/** One approved, still-open PR — the `/v1/approved-prs` projection. */
export type ApprovedPrRow = {
  readonly id: string;
  readonly repo_full_name: string;
  readonly github_number: number;
  /** Human PR title; null when the platform never saw one. */
  readonly title: string | null;
  readonly author_login: string | null;
  readonly opened_iso: string;
  /** The approval instant (`merge_decided_at`); null when it was never recorded. */
  readonly approved_iso: string | null;
  readonly ci_status: string;
  /** GitHub's mergeability; null when GitHub has never computed it. */
  readonly mergeable: ApprovedPrMergeable | null;
  /** GitHub's own block (`blocked`, `behind`, `draft`), when it has one. */
  readonly merge_block_state: string | null;
  /** The sentence naming who holds GitHub's block. */
  readonly merge_block_reason: string | null;
  /** GitHub's auto-merge is armed on the PR. */
  readonly auto_merge: boolean;
  /**
   * The refusal the STORED row proves, as the merge gate names it:
   * `head_moved` (the approval is pinned to a head that is no longer the live
   * head) or `github_blocks` (GitHub's own stored block) — the domain's closed
   * set, `StoredMergeRefusalCode`. Null when the stored row proves neither:
   * such a row shows its live waiting state instead.
   */
  readonly merge_refusal_code: StoredMergeRefusalCode | null;
};

/**
 * The label each refusal code renders — a CLOSED record over the domain's set,
 * so a code added to {@link StoredMergeRefusalCode} without a label is a
 * compile error here, and `src/i18n.test.ts` holds the catalog side (a label
 * that exists here but not in `locales/en.json` fails there rather than
 * shipping `⟦key⟧`).
 */
export const APPROVED_PR_REFUSAL_LABEL_KEY: Readonly<Record<StoredMergeRefusalCode, string>> = {
  head_moved: 'approved_prs.refusal_head_moved',
  github_blocks: 'approved_prs.refusal_github_blocks',
};

/**
 * GitHub's mergeability as a label — never a raw wire word. `unknown` covers
 * both the stored word and a value this build does not know: an unreadable
 * mergeability is not a mergeable one.
 */
export const APPROVED_PR_MERGEABLE_LABEL_KEY: Readonly<Record<ApprovedPrMergeable, string>> = {
  mergeable: 'approved_prs.mergeable',
  conflicting: 'approved_prs.conflicting',
  unknown: 'approved_prs.mergeability_unknown',
};

export const APPROVED_PRS_QUERY_KEY = 'account_approved_prs';

/**
 * The endpoint's own ceiling (`MAX_LIMIT`, `apps/api/src/routes/overview.ts`) —
 * the `APPROVALS_LIMIT` precedent: a bounded head that mints no cursor, so
 * `limit` IS the page size and there is no second page to walk.
 */
export const APPROVED_PRS_LIMIT = 100;

/**
 * Approved open PRs, account-wide. Idle with no session.
 *
 * POLLED, not streamed: no SSE topic carries this list (the `approvals` topic
 * is the PENDING queue's), and the gate merges these PRs server-side — a row
 * approved an hour ago can be merged by the next hop, so the list re-reads
 * rather than showing a PR that is no longer open. A decision taken in the
 * queue re-reads it at once (`approval-queue.ts`): an approval moves the PR here.
 */
export function useApprovedPrs(token: string | null = SESSION_TOKEN) {
  return createQuery<readonly ApprovedPrRow[]>(() => ({
    queryKey: [APPROVED_PRS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    refetchInterval: 30_000,
    queryFn: async () =>
      (
        await apiFetch<{ data: readonly ApprovedPrRow[] }>(
          `/v1/approved-prs?limit=${APPROVED_PRS_LIMIT}`,
          token,
        )
      ).data,
  }));
}
